import axios from '@/libs/axios'

import moment from '@/libs/moment'
import shipments from '@/apis/shipments'

/**
 @typedef {Object} ExpeditionSheet
 @property {Number} id
 @property {string} uuid
 @property {string} datetime
 @property {Number} plot_id
 @property {Number} crop_id
 @property {Number} crop_type_id
 @property {Number} crop_grade_id
 @property {Number} crop_packing_id
 @property {Number} document_template_id
 @property {Number} quantity
 @property {string} state
 @property {string} send_at
 @property {string} created_at
 @property {string} updated_at
 @property {string} deleted_at
 */

/**
 * @typedef {Object} Family
 * @property {string} slug - Slug de la famille.
 */

/**
 * @typedef {Object} Crop
 * @property {string} id - Identifiant de la culture.
 * @property {string} name - Nom de la culture.
 * @property {string} emoji - Emoji représentant la culture.
 * @property {string} color - Couleur associée à la culture.
 * @property {Family} family - Famille de la culture.
 */

/**
 * @typedef {Object} Type
 * @property {string} id - Identifiant du type.
 * @property {string} name - Nom du type.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total.
 * @property {number} quantity - Quantité totale.
 */

/**
 * @typedef {Object} Packing
 * @property {string} id - Identifiant du conditionnement.
 * @property {string} name - Nom du conditionnement.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total des conditionnements.
 * @property {number} quantity - Quantité totale des conditionnements.
 */

/**
 * @typedef {Object} Plot
 * @property {string} id - Identifiant de la parcelle.
 * @property {string} name - Nom de la parcelle.
 * @property {string} abbreviation - Abréviation de la parcelle.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total de la parcelle.
 * @property {number} quantity - Quantité totale de la parcelle.
 */

/**
 * @typedef {Object} Account
 * @property {string} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 * @property {number[]} weight - Liste des poids individuels gérés par ce compte.
 * @property {number[]} quantities - Liste des quantités individuelles gérées par ce compte.
 * @property {number} total_weight - Poids total associé au compte.
 * @property {number} quantity - Quantité totale associée au compte.
 */

/**
 * @typedef {Object} Stats
 * @property {Crop} crop - Détails de la culture.
 * @property {Object<string, Type>} types - Liste des types indexés par identifiants.
 * @property {Object<string, Packing>} packings - Liste des conditionnements indexés par identifiants.
 * @property {Object<string, Plot>} plots - Liste des parcelles indexées par identifiants.
 * @property {string[]} options - Liste des options disponibles.
 * @property {Object<string, Account>} accounts - Liste des comptes indexés par identifiants.
 * @property {Array} grades - Liste des grades (actuellement vide).
 * @property {number} total_weight - Poids total global.
 * @property {number} quantity - Quantité totale globale.
 */

/**
 * @typedef {Object} Account
 * @property {number} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 */

/**
 * @typedef {Object} Address
 * @property {number} id - Identifiant de l'adresse.
 * @property {string} complete_address - Adresse complète.
 * @property {Account} account - Compte associé à l'adresse.
 */

/**
 * @typedef {Object} Farmer
 * @property {string} name - Nom de l'agriculteur.
 */

/**
 * @typedef {Object} Carrier
 * @property {number} id - Identifiant du transporteur.
 * @property {string} name - Nom du transporteur.
 */

/**
 * @typedef {Object} ExpeditionSheet
 * @property {number} id - Identifiant de la feuille d'expédition.
 * @property {string} datetime - Date et heure de l'expédition (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État de la feuille d'expédition.
 * @property {string} external_id - Identifiant externe de la feuille d'expédition.
 * @property {string} pdf_url - URL du PDF associé.
 * @property {boolean} is_valid - Indique si la feuille est valide.
 * @property {Farmer} farmer - Agriculteur associé.
 * @property {Account} account - Compte associé à l'expédition.
 * @property {Carrier|null} carrier - Transporteur associé (nullable).
 */

/**
 * @typedef {Object} DeliverySheet
 * @property {string} external_id - Identifiant externe de la livraison.
 * @property {string} json_url - URL du fichier JSON associé.
 * @property {string} xml_url - URL du fichier XML associé.
 * @property {string} pdf_url - URL du fichier PDF associé.
 * @property {Farmer} farmer - Agriculteur associé à cette livraison.
 */

/**
 * @typedef {Object} Delivery
 * @property {string} datetime - Date et heure principales (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État principal de l'objet.
 * @property {string} external_id - Identifiant externe principal.
 * @property {boolean} is_valid - Indique si l'objet principal est valide.
 * @property {Farmer[]} farmers - Liste des agriculteurs associés.
 * @property {ExpeditionSheet[]} expedition_sheets - Liste des feuilles d'expédition associées.
 * @property {DeliverySheet[]} delivery_sheets - Liste des feuilles de livraison associées.
 * @property {Account} account - Compte principal associé.
 * @property {Address} account_shipping_address - Adresse de livraison associée au compte.
 * @property {Carrier} carrier - Transporteur principal.
 */

export default {
  namespaced: true,
  state: {
    /** @type {null|Array<Stats>} list */
    stats: null,
    /** @type {string|null} lastPull */
    lastPull: null,
  },
  getters: {
    /**
     * @param state
     * @returns {boolean}
     */
    isFetch(state) {
      return state.stats !== null
        && state.lastPull !== null
    },
    /**
     * @param state
     * @returns {Array<Stats>}
     */
    getStats(state) {
      return state.stats
    },
    /**
     * @param state
     * @returns {moment|null}
     */
    getLastPull(state) {
      return state.lastPull ? moment(state.lastPull) : null
    },
    /**
     * @param state
     * @returns {number}
     */
    count(state) {
      return state.stats !== null ? state.stats.length : 0
    },
    /**
     * @param state
     * @returns {boolean}
     */
    has(state) {
      return state.stats !== null && state.stats.length > 0
    },
  },
  mutations: {
    clear(state) {
      state.stats = null
      state.lastPull = null
    },
    /**
     * @param state
     * @param stats
     *
     * @returns {Promise<void>}
     */
    set(state, stats) {
      state.stats = stats
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD HH:mm:ss')
    },
  },
  actions: {
    /**
     * @param commit
     * @param getters
     * @param rootGetters
     * @returns {Promise<boolean>}
     */
    async fetch({
      commit,
      rootGetters,
    }) {
      const response = await (axios(await (shipments.stats(
        rootGetters['auth/getToken'],
        rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
        moment()
          .locale('en')
          .format('YYYY-MM-DD'),
      ))))

      commit('set', response.data)
      return true
    },
  },
}
